import { Subject } from 'rxjs';
import { DirectoryEntities, DirectoryResponse } from '@nice-ccf/common-sdk';
import { AddressBookService } from './service/address-book-service';
import { SkillService } from './service/skill-service';
import { CXoneDirectoryProvider } from './provider/cxone-directory-provider';

/** This is the base class for Directory*/
export class CXoneDirectory {
  private static singleton: CXoneDirectory;
  public addressBookService: AddressBookService;
  public skillService: SkillService;
  public directoryEvent = new Subject<DirectoryResponse>();
  private directoryProvider: CXoneDirectoryProvider = new CXoneDirectoryProvider();

  /**
   * Create instance for directory session and initialize it
   * @example
   * ```
   * new DirectoryBase();
   * ```
   */
  constructor() {
    this.directoryProvider.setDirectoryBaseInstance(this);
    this.addressBookService = new AddressBookService();
    this.skillService = new SkillService();
  }

  /**
   * Method to create singleton object of the class
   * ```
   * @example
   * const DirectoryBase = DirectoryBase.instance();
   * ```
   */
  public static get instance(): CXoneDirectory {
    if (!CXoneDirectory.singleton) {
      CXoneDirectory.singleton = new CXoneDirectory();
      CXoneDirectory.singleton.clearDirectoryDB();// we will clear the directory cache data before every new instance creation
    }
    return CXoneDirectory.singleton;
  }

  /**
   * Used to clear out the directory data from the index DB before initiating a new directory instance
  */
  private clearDirectoryDB(){
    this.directoryProvider.clearDirectoryDB(); // to clear out the indexDB directory cache data before starting polling
  }

  /**
   * Used to handle the directory data for polling, non polling and search requests
   * @param isPolling - polling flag to identify if polling is needed or not 
   * @param entity - requested entity array for directory data
   * @param offset - start index for the pagination data, should be greater than 0
   * @param limit - end index for the pagination data, should be greater than 0
   * @param searchText - search string in case for search request, search will happen on fields depending on entity requested.
   * If requested entity is agentList then search will happen based on fields - (firstName, lastName, userName).
   * If requested entity is skillList then search will happen based on field - (skillName).
   * If requested entity is addressBookList then search will happen based on field - (firstName, lastName, mobile, phone, email).
   * @example -
   * ```
   * getDirectoryData(true, [], 1, 50, '');
   * ```
   */
  public getDirectoryData(isPolling?: boolean, entity?: DirectoryEntities[], offset?: number, limit?: number, searchText?: string) {
    entity = entity?.length ? entity: [DirectoryEntities.ADDRESS_BOOK_LIST, DirectoryEntities.AGENT_LIST, DirectoryEntities.SKILL_LIST];
    this.directoryProvider.getDirectoryData(entity, isPolling, offset, limit, searchText);
  }

  /**
   * Used to terminate directory polling
   * @example -
   * ```
   * this.directoryBase.terminateDirectoryPolling()
   * ```
   */
  public terminateDirectoryPolling() {
    this.directoryProvider.terminatePolling();
  }
 
}
